import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { motion } from 'framer-motion';
import { useMedia } from 'react-use';
import Heading from 'components/shared/heading';
import styles from './styles.module.scss';

const ANIMATION_DURATION = 0.5;

const TabFeaturesAndImage = ({
  items = [],
  title = null,
  titleTag = 'h2',
  description = null,
  background = 'white',
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const itemRefs = useRef({});
  const isMobile = useMedia('(max-width: 1023px)', false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (items && items.length > 0) {
      items.forEach((item, index) => {
        if (item?.image?.id) {
          const parent = containerRef?.current?.parentElement;
          const imageSource = parent?.querySelector(`#img-${item.image.id}`);

          // Desktop container
          const desktopImageDiv = parent?.querySelector(
            `#tab-features-img-box-${item.image.id}-${index}`
          );
          if (desktopImageDiv && imageSource) {
            desktopImageDiv.innerHTML = '';
            desktopImageDiv.appendChild(imageSource.cloneNode(true));
          }

          // Mobile container
          const mobileImageDiv = parent?.querySelector(
            `#tab-features-img-box-mobile-${item.image.id}-${index}`
          );
          if (mobileImageDiv && imageSource) {
            mobileImageDiv.innerHTML = '';
            mobileImageDiv.appendChild(imageSource.cloneNode(true));
          }
        }
      });
    }
  }, [items]);

  const onItemPress = useCallback(
    (index) => () => {
      const isSame = index === activeIndex;
      if (!isSame) {
        setActiveIndex(index);
      }

      if (isMobile && !isSame) {
        setTimeout(
          () => {
            const element = itemRefs.current[index];
            if (element) {
              const buttonPosition = element.getBoundingClientRect().top;
              const offsetPosition = buttonPosition + window.pageYOffset - 80;
              window.scrollTo({
                top: Math.max(0, offsetPosition),
                behavior: 'smooth',
              });
            }
          },
          ANIMATION_DURATION * 1000 + 50
        );
      }
    },
    [activeIndex, isMobile]
  );

  const handleKeyDown = useCallback(
    (index) => (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onItemPress(index)();
      }
    },
    [onItemPress]
  );

  const setItemRef = useCallback(
    (index) => (el) => {
      if (el) itemRefs.current[index] = el;
    },
    []
  );

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <section
      className={cn(styles.tabFeatures, {
        [styles.backgroundWhite]: background === 'white',
        [styles.backgroundGray]: background === 'gray',
      })}
      ref={containerRef}
    >
      <div className="container">
        <div className={styles.header}>
          {title && (
            <Heading
              tag={titleTag}
              size="xl"
              color="primary"
              highlightedWordsColor="primary"
              highlightedWordsWithoutWrap
              className={styles.title}
            >
              {title}
            </Heading>
          )}
          {description && (
            <div className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />
          )}
        </div>
        <div className={styles.content}>
          <div className={styles.items}>
            {items.map((item, index) => (
              <div
                key={index}
                className={cn(styles.item, {
                  [styles.active]: index === activeIndex,
                })}
                role="button"
                tabIndex={index}
                onClick={onItemPress(index)}
                onKeyDown={handleKeyDown(index)}
                ref={setItemRef(index)}
              >
                <div className={styles.itemHeader}>
                  <span className={styles.itemNumber}>
                    {index < 9 ? `0${index + 1}.` : `${index + 1}.`}
                  </span>
                  <h3 className={styles.itemTitle}>{item.title}</h3>
                </div>
                <motion.div
                  initial="hidden"
                  animate={index === activeIndex ? 'visible' : 'hidden'}
                  variants={{
                    hidden: { height: 0, opacity: 0, marginTop: 0 },
                    visible: { height: 'auto', opacity: 1, marginTop: 0 },
                  }}
                  transition={{ duration: ANIMATION_DURATION, ease: [0.25, 0.1, 0.25, 1] }}
                  className={styles.contentWrapper}
                >
                  <div className={styles.contentInner}>
                    <motion.div
                      className={styles.mobileImage}
                      id={`tab-features-img-box-mobile-${item?.image?.id}-${index}`}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={
                        index === activeIndex
                          ? { opacity: 1, scale: 1 }
                          : { opacity: 0, scale: 0.95 }
                      }
                      transition={{ delay: 0.1, duration: ANIMATION_DURATION }}
                    />
                    <motion.div
                      className={styles.itemDescription}
                      dangerouslySetInnerHTML={{ __html: item.description }}
                      initial={{ opacity: 0 }}
                      animate={index === activeIndex ? { opacity: 1 } : { opacity: 0 }}
                      transition={{ delay: 0.1, duration: ANIMATION_DURATION }}
                    />
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
          <div className={styles.images}>
            {items.map((item, index) => (
              //  Placeholder for helper function to inject image by ID if using that pattern
              <div
                id={`tab-features-img-box-${item?.image?.id}-${index}`}
                key={index}
                className={cn(styles.imageItem, {
                  [styles.active]: index === activeIndex,
                })}
                tabIndex={index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

TabFeaturesAndImage.propTypes = {
  title: PropTypes.string,
  titleTag: PropTypes.oneOf(['h1', 'h2', 'h3']),
  description: PropTypes.string,
  background: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.shape({
        id: PropTypes.number,
      }),
    })
  ),
};

export default TabFeaturesAndImage;
